import type { PdfFormDefinition, PdfJoinField, DispatchFn, RpcFn } from "./types";
import { BODY_INJURY_MARKERS, BODY_INJURY_MARKS_KEY, BURN_ZONE_MARKERS, BURN_ZONES_KEY, BURN_RED_THRESHOLD } from "./bodyInjuries";

export const PDF_RENDER_SCALE = 2;
export const PDF_PAGE_H = 841.89; // A4 height in pts
export const PDF_PAGE_W = 595.28; // A4 width in pts

const RENDER_SCALE = PDF_RENDER_SCALE;

export interface RawPdfPages {
  pageCanvases: HTMLCanvasElement[];
  pageItems: { str: string; transform: number[] }[][];
  lines: string[];
  numPages: number;
}

export interface ParsedPdf {
  extracted: Record<string, string | boolean>;
  pageCanvases: HTMLCanvasElement[];
  lines: string[];
}

/** Render all pages of a PDF file into canvases and extract raw text items. */
export async function renderPdfPages(file: File): Promise<RawPdfPages> {
  const { getDocument, GlobalWorkerOptions } = await import("pdfjs-dist");
  GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
  ).toString();

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await getDocument({ data: arrayBuffer }).promise;

  const pageResults = await Promise.all(
    Array.from({ length: pdf.numPages }, async (_, i) => {
      const page = await pdf.getPage(i + 1);
      const viewport = page.getViewport({ scale: RENDER_SCALE });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const [content] = await Promise.all([
        page.getTextContent(),
        page.render({ canvas, viewport }).promise,
      ]);
      return { canvas, content };
    })
  );

  const lines: string[] = [`=== ${file.name} (${pdf.numPages} pages) ===\n`];
  const pageCanvases: HTMLCanvasElement[] = [];
  const pageItems: { str: string; transform: number[] }[][] = [];
  for (let i = 0; i < pageResults.length; i++) {
    lines.push(`\n--- Page ${i + 1} ---`);
    const { canvas, content } = pageResults[i];
    pageCanvases.push(canvas);
    const items: { str: string; transform: number[] }[] = [];
    pageItems.push(items);
    for (const item of content.items) {
      if ("str" in item && (item as { str: string }).str.trim()) {
        const tx = (item as { transform: number[] }).transform;
        const str = (item as { str: string }).str;
        items.push({ str, transform: tx });
        lines.push(`[x:${tx[4].toFixed(0)} y:${tx[5].toFixed(0)}] ${str}`);
      }
    }
  }

  return { pageCanvases, pageItems, lines, numPages: pdf.numPages };
}

const FORM_001_ANCHORS = [
  { page: 0, x: 19,  y: 817, text: "Форма первинної облікової документації № 001/о" },
  { page: 0, x: 341, y: 816, text: "ПЕРВИННА МЕДИЧНА КАРТКА" },
  { page: 1, x: 242, y: 813, text: "Надана допомога роль 2" },
  { page: 1, x: 23,  y: 799, text: "Прибуття на роль 2:" },
] as const;

/**
 * Returns true if the two pages at pageOffset/pageOffset+1 look like form 001/о.
 * Checks fixed template labels at known coordinates — fast, zero extra rendering.
 */
export function isForm001(
  pageItems: { str: string; transform: number[] }[][],
  pageOffset: number,
): boolean {
  return FORM_001_ANCHORS.every(({ page, x, y, text }) =>
    findNear(pageItems[pageOffset + page] ?? [], x, y, 6, 4) === text
  );
}

/**
 * Extract form fields for one case from pre-rendered pages.
 * pageOffset is the index of the first page for this case (0-based).
 * Form field definitions use page numbers 1 and 2, which map to
 * pageOffset+0 and pageOffset+1 in the full pages array.
 */
export function extractCaseFields(
  pageCanvases: HTMLCanvasElement[],
  pageItems: { str: string; transform: number[] }[][],
  pageOffset: number,
  form: PdfFormDefinition,
): Record<string, string | boolean> {
  const extracted: Record<string, string | boolean> = {};
  for (const f of form.textFields) {
    const items = pageItems[pageOffset + f.page - 1] ?? [];
    extracted[f.key] = f.concat
      ? findNearConcat(items, f.x, f.y, f.xTol, f.yTol, f.xMin)
      : findNear(items, f.x, f.y, f.xTol, f.yTol, f.xMin);
  }

  const pageImageDatas = [
    pageCanvases[pageOffset].getContext("2d")!.getImageData(0, 0, pageCanvases[pageOffset].width, pageCanvases[pageOffset].height),
    pageCanvases[pageOffset + 1].getContext("2d")!.getImageData(0, 0, pageCanvases[pageOffset + 1].width, pageCanvases[pageOffset + 1].height),
  ];
  for (const f of form.checkboxFields)
    extracted[f.key] = sampleCheckboxFromData(pageImageDatas[f.page - 1], f.x, f.y);

  if (form.bodyInjuryPage !== undefined) {
    const imageData = pageImageDatas[form.bodyInjuryPage - 1];

    // Trauma X-mark detection
    const foundMarks: { x: number; y: number }[] = [];
    for (const m of BODY_INJURY_MARKERS) {
      // X marks are thin diagonal lines — use a lower threshold than checkboxes
      if (sampleCheckboxFromData(imageData, m.pdfX, m.pdfY, 2, m.radius, 0.08)) {
        foundMarks.push({ x: Math.round(m.canonicalXPct * 10) / 10, y: Math.round(m.canonicalYPct * 10) / 10 });
      }
    }
    if (foundMarks.length > 0) extracted[BODY_INJURY_MARKS_KEY] = JSON.stringify(foundMarks);

    // Burn zone detection — check each region for red pixel coverage
    const foundBurns: string[] = [];
    for (const b of BURN_ZONE_MARKERS) {
      if (sampleRedInPolygon(imageData, b.points) >= (b.threshold ?? BURN_RED_THRESHOLD)) {
        foundBurns.push(b.zoneId);
      }
    }
    if (foundBurns.length > 0) extracted[BURN_ZONES_KEY] = JSON.stringify(foundBurns);
  }

  return extracted;
}

/** Convenience wrapper for single-case PDFs (2 pages). */
export async function parsePdfFile(file: File, form: PdfFormDefinition): Promise<ParsedPdf> {
  const { pageCanvases, pageItems, lines } = await renderPdfPages(file);
  const extracted = extractCaseFields(pageCanvases, pageItems, 0, form);
  return { extracted, pageCanvases, lines };
}

export async function resolveUnitField(
  unitName: string,
  patientUuid: string,
  dispatch: DispatchFn,
  rpc: RpcFn,
  createUuid: () => string,
): Promise<void> {
  try {
    const result = await rpc<{ units: { id: string; name: string | null }[] }>({
      type: "searchUnits",
      search: unitName,
    });
    const exact = result.units.find((u) => u.name?.toLowerCase() === unitName.toLowerCase());
    let unitUuid: string;
    if (exact) {
      unitUuid = exact.id;
    } else {
      unitUuid = createUuid();
      await dispatch("unit.create", { uuid: unitUuid });
      await dispatch("unit.setName", { uuid: unitUuid, name: unitName });
    }
    await dispatch("patient.setUnit", { uuid: patientUuid, unitUuid });
  } catch (err) {
    console.warn("[PDF import] Unit lookup/create failed for:", unitName, err);
  }
}


/** Format a local timestamp as YYYY-MM-DD string. */
export function formatLocalDate(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function applyJoinFields(
  fields: Record<string, string | boolean>,
  joins: PdfJoinField[] = [],
): Record<string, string | boolean> {
  if (!joins.length) return fields;
  const result = { ...fields };
  for (const { key, sources, separator = " " } of joins) {
    result[key] = sources
      .map((s) => result[s] as string | undefined)
      .filter(Boolean)
      .join(separator);
  }
  return result;
}

/** Find nearby text items sorted by distance. Shared by findNear/findNearConcat. */
function findNearHits(
  items: { str: string; transform: number[] }[],
  tx: number, ty: number, xTol = 15, yTol = 8, xMin?: number,
) {
  const hits = items.filter(
    (it) =>
      Math.abs(it.transform[4] - tx) <= xTol &&
      Math.abs(it.transform[5] - ty) <= yTol &&
      (xMin === undefined || it.transform[4] >= xMin),
  );
  hits.sort(
    (a, b) =>
      Math.abs(a.transform[4] - tx) + Math.abs(a.transform[5] - ty) -
      (Math.abs(b.transform[4] - tx) + Math.abs(b.transform[5] - ty)),
  );
  return hits;
}

export function findNear(
  items: { str: string; transform: number[] }[],
  tx: number, ty: number, xTol = 15, yTol = 8, xMin?: number,
): string {
  const hits = findNearHits(items, tx, ty, xTol, yTol, xMin);
  return hits.length ? hits[0].str.trim() : "";
}

/**
 * Like findNear, but concatenates adjacent text fragments on the same line.
 * Use only for fields where PDFs split text into multiple items (e.g. names).
 */
export function findNearConcat(
  items: { str: string; transform: number[] }[],
  tx: number, ty: number, xTol = 15, yTol = 8, xMin?: number,
): string {
  const hits = findNearHits(items, tx, ty, xTol, yTol, xMin);
  if (!hits.length) return "";
  const best = hits[0];
  const sameLine = hits
    .filter((h) => Math.abs(h.transform[5] - best.transform[5]) <= 2)
    .sort((a, b) => a.transform[4] - b.transform[4]);
  if (sameLine.length > 1) {
    // Walk outward from best, chaining fragments within 15pt gap
    const bestIdx = sameLine.indexOf(best);
    const chain = [best];
    const isAdjacent = (a: typeof best, b: typeof best) =>
      b.transform[4] - a.transform[4] - (a.str.length * 4) < 15;
    for (let i = bestIdx - 1; i >= 0; i--) {
      if (isAdjacent(sameLine[i], chain[0])) chain.unshift(sameLine[i]);
      else break;
    }
    for (let i = bestIdx + 1; i < sameLine.length; i++) {
      if (isAdjacent(chain[chain.length - 1], sameLine[i])) chain.push(sameLine[i]);
      else break;
    }
    if (chain.length > 1) return chain.map((h) => h.str.trim()).filter(Boolean).join("");
  }
  return best.str.trim();
}

/** Sample a checkbox from pre-fetched full-page ImageData. Avoids a
 *  getImageData call per checkbox — fetch the ImageData once per page and
 *  pass it here for all checkboxes on that page. */
export function sampleCheckboxFromData(
  imageData: ImageData,
  pdfX: number,
  pdfY: number,
  scale = 2,
  radius = 3,
  threshold = 0.5,
): boolean {
  const cx = Math.round(pdfX * scale);
  const cy = Math.round((PDF_PAGE_H - pdfY) * scale);
  const { data, width, height } = imageData;
  let dark = 0;
  let total = 0;
  for (let dy = -radius; dy < radius; dy++) {
    for (let dx = -radius; dx < radius; dx++) {
      const px = cx + dx;
      const py = cy + dy;
      if (px < 0 || py < 0 || px >= width || py >= height) continue;
      if (data[(py * width + px) * 4] < 80) dark++;
      total++;
    }
  }
  return total > 0 && dark / total > threshold;
}

function pointInPolygon(px: number, py: number, poly: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi)
      inside = !inside;
  }
  return inside;
}

/**
 * Sample a polygon PDF region for red-dominant pixels (burn detection).
 * Points are PDF-point coords (bottom-left origin). Returns fraction of
 * pixels inside the polygon that are red-dominant.
 */
export function sampleRedInPolygon(
  imageData: ImageData,
  points: [number, number][],
  scale = 2,
): number {
  // Convert PDF points → canvas coords (top-left origin)
  const canvasPts = points.map(([px, py]) => [px * scale, (PDF_PAGE_H - py) * scale] as [number, number]);
  const xs = canvasPts.map((p) => p[0]);
  const ys = canvasPts.map((p) => p[1]);
  const x0 = Math.floor(Math.min(...xs));
  const x1 = Math.ceil(Math.max(...xs));
  const y0 = Math.floor(Math.min(...ys));
  const y1 = Math.ceil(Math.max(...ys));
  const { data, width, height } = imageData;
  let red = 0, total = 0;
  for (let cy = y0; cy <= y1; cy++) {
    for (let cx = x0; cx <= x1; cx++) {
      if (cx < 0 || cy < 0 || cx >= width || cy >= height) continue;
      if (!pointInPolygon(cx, cy, canvasPts)) continue;
      const i = (cy * width + cx) * 4;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      if (r > 150 && r > g + 30 && r > b) red++;
      total++;
    }
  }
  return total > 0 ? red / total : 0;
}

/**
 * Parse a time-of-day string "HH MM" into a UTC timestamp (ms) for today at that time.
 * Consistent with manual entry: the DateTimePicker defaults to today when no timestamp exists.
 * Returns null if malformed or invalid.
 */
export function parseTimeHHMM(value: string): number | null {
  const p = value.trim().split(/\s+/).map(Number);
  if (p.length < 2 || p.some(isNaN)) return null;
  const [h, min] = p;
  const now = new Date();
  // Use local time — PDF times are written in the medic's local timezone,
  // and fmtTime/getHours() displays in local time too.
  const ts = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, min).getTime();
  return isNaN(ts) ? null : ts;
}

/**
 * Parse event date+time string into a UTC timestamp (ms).
 * Accepts:
 *   "HH MM DD MM YY"    (5 space-separated parts, with time)
 *   "HH:MM DD MM YY"    (4 parts, time as colon-separated "HH:MM")
 *   "DD MM YY"          (3 parts, midnight UTC)
 * Returns null if malformed or invalid.
 */
export function parseDMY(value: string): number | null {
  const raw = value.trim().split(/\s+/);
  // "HH:MM DD MM YY" — 4 parts where the first contains a colon
  let parts: number[];
  if (raw.length === 4 && raw[0].includes(":")) {
    const [hStr, mStr] = raw[0].split(":");
    parts = [Number(hStr), Number(mStr), ...raw.slice(1).map(Number)];
  } else {
    parts = raw.map(Number);
  }
  if (parts.some(isNaN)) return null;
  let h = 0, min = 0, d: number, mo: number, y: number;
  if (parts.length >= 5) {
    [h, min, d, mo, y] = parts;
  } else if (parts.length >= 3) {
    [d, mo, y] = parts;
  } else {
    return null;
  }
  const year = y < 100 ? 2000 + y : y;
  // Use local time — PDF times are in the medic's local timezone
  const ts = new Date(year, mo - 1, d, h, min).getTime();
  return isNaN(ts) ? null : ts;
}

/**
 * Returns all field keys that should be looked up in `fields` and dispatched.
 * Includes text/checkbox keys plus any joinField target keys not already covered.
 */
export function getDispatchKeys(form: PdfFormDefinition): string[] {
  const keys = [
    ...form.textFields.map((f) => f.key),
    ...form.checkboxFields.map((f) => f.key),
  ];
  const keySet = new Set(keys);
  for (const jf of form.joinFields ?? []) {
    if (!keySet.has(jf.key)) keys.push(jf.key);
  }
  return keys;
}
